import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DraftField, DraftSection } from "@sga/shared";

/**
 * The centre pane: pages → sections → rows → fields, with inline label editing and a per-field
 * action menu (docs/04-BUILD-PLAN.md Phase 4).
 */

const visibilityBadge = (field: DraftField): { label: string; className: string } | null => {
  if (field.visibility === "admin_only") {
    return { label: "Admin only", className: "bg-brand-red/10 text-brand-red" };
  }
  if (field.visibility === "hidden") {
    return { label: "Hidden", className: "bg-muted text-muted-foreground" };
  }
  return null;
};

const FieldCard = ({
  field,
  typeLabel,
  isSelected,
  onSelect,
  onRelabel,
  onDuplicate,
  onDelete,
  onMoveToSection,
  onToggleSideBySide,
  sections,
}: {
  field: DraftField;
  typeLabel: string;
  isSelected: boolean;
  onSelect: () => void;
  onRelabel: (label: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMoveToSection: (sectionKey: string | null) => void;
  onToggleSideBySide: () => void;
  sections: DraftSection[];
}): JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.key,
    data: { source: "canvas", field },
  });

  const badge = visibilityBadge(field);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded-md border bg-card p-2 ${isDragging ? "opacity-50" : ""} ${
        isSelected ? "ring-2 ring-primary" : ""
      } ${field.visibility !== "visible" ? "border-dashed" : ""}`}
      onClick={onSelect}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="mt-0.5 cursor-grab rounded px-1 text-muted-foreground hover:bg-secondary"
          aria-label={`Reorder ${field.label}`}
        >
          ⠿
        </button>

        <div className="min-w-0 flex-1">
          {/* Inline label editing — no modal, no separate save. */}
          <input
            value={field.label}
            onChange={(event) => onRelabel(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            className="w-full truncate rounded border-transparent bg-transparent px-1 text-sm font-medium hover:border-border focus:border-border"
            aria-label={`Label for ${field.key}`}
          />
          <div className="mt-0.5 flex flex-wrap items-center gap-1 px-1">
            <span className="text-[11px] text-muted-foreground">{typeLabel}</span>
            <code className="text-[11px] text-muted-foreground">{field.key}</code>
            {field.isRequired && <span className="text-[11px] text-brand-red">required</span>}
            {badge && (
              <span className={`rounded px-1 text-[10px] ${badge.className}`}>{badge.label}</span>
            )}
            {field.isPii && (
              <span className="rounded bg-accent/15 px-1 text-[10px] text-primary">PII</span>
            )}
            {field.isSensitive && (
              <span className="rounded bg-brand-red/10 px-1 text-[10px] text-brand-red">
                sensitive
              </span>
            )}
            {field.rowGroup !== 0 && (
              <span className="rounded bg-secondary px-1 text-[10px]">side by side</span>
            )}
          </div>
        </div>

        {/* Per-field action menu. A <details> rather than a popover library — one fewer dependency
            and it is keyboard operable for free. */}
        <details className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
          <summary
            className="cursor-pointer list-none rounded px-1.5 text-muted-foreground hover:bg-secondary"
            aria-label={`Actions for ${field.label}`}
          >
            ⋯
          </summary>
          <div className="absolute right-0 z-10 mt-1 w-52 space-y-0.5 rounded-md border bg-card p-1 shadow-md">
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-secondary"
              onClick={onDuplicate}
            >
              Duplicate
            </button>
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs hover:bg-secondary"
              onClick={onToggleSideBySide}
            >
              {field.rowGroup === 0 ? "Place side by side with previous" : "Give its own row"}
            </button>
            <div className="border-t pt-1">
              <label className="block px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                Move to section
              </label>
              <select
                className="w-full rounded border px-1.5 py-1 text-xs"
                value={field.sectionKey ?? ""}
                onChange={(event) => onMoveToSection(event.target.value || null)}
                aria-label={`Move ${field.label} to section`}
              >
                <option value="">(no section)</option>
                {sections.map((section) => (
                  <option key={section.key} value={section.key}>
                    {section.title ?? section.key}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-left text-xs text-brand-red hover:bg-secondary"
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
        </details>
      </div>
    </div>
  );
};

export interface CanvasProps {
  fields: DraftField[];
  sections: DraftSection[];
  selectedKey: string | null;
  typeLabelFor: (type: string) => string;
  onSelect: (key: string) => void;
  onFieldChange: (key: string, next: DraftField) => void;
  onDuplicate: (key: string) => void;
  onDelete: (key: string) => void;
  onAddSection: () => void;
  onSectionChange: (key: string, next: DraftSection) => void;
  /**
   * Joining a row touches the field above as well, so this cannot be expressed through
   * `onFieldChange` — see builder/rowGroups.ts for why setting one field's rowGroup alone did
   * nothing at all.
   */
  onToggleSideBySide: (key: string) => void;
}

export const Canvas = ({
  fields,
  sections,
  selectedKey,
  typeLabelFor,
  onSelect,
  onFieldChange,
  onDuplicate,
  onDelete,
  onAddSection,
  onSectionChange,
  onToggleSideBySide,
}: CanvasProps): JSX.Element => {
  const pages = [...new Set(sections.map((section) => section.pageNumber))].sort((a, b) => a - b);
  const pagesToRender = pages.length > 0 ? pages : [1];

  const renderField = (field: DraftField): JSX.Element => (
    <FieldCard
      key={field.key}
      field={field}
      typeLabel={typeLabelFor(field.type)}
      isSelected={selectedKey === field.key}
      sections={sections}
      onSelect={() => onSelect(field.key)}
      onRelabel={(label) => onFieldChange(field.key, { ...field, label })}
      onDuplicate={() => onDuplicate(field.key)}
      onDelete={() => onDelete(field.key)}
      onMoveToSection={(sectionKey) => onFieldChange(field.key, { ...field, sectionKey })}
      onToggleSideBySide={() => onToggleSideBySide(field.key)}
    />
  );

  const unsectioned = fields.filter((field) => !field.sectionKey);

  return (
    <div className="flex-1 overflow-y-auto bg-background p-6">
      <SortableContext
        items={fields.map((field) => field.key)}
        strategy={verticalListSortingStrategy}
      >
        {unsectioned.length > 0 && (
          <div className="mb-6 space-y-2">{unsectioned.map(renderField)}</div>
        )}

        {pagesToRender.map((pageNumber) => (
          <div key={pageNumber} className="mb-8">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground">
                Page {pageNumber}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>

            {sections
              .filter((section) => section.pageNumber === pageNumber)
              .sort((a, b) => a.position - b.position)
              .map((section) => (
                <section key={section.key} className="mb-4 rounded-lg border bg-card/40 p-3">
                  <input
                    value={section.title ?? ""}
                    placeholder="Section title"
                    onChange={(event) =>
                      onSectionChange(section.key, { ...section, title: event.target.value })
                    }
                    className="mb-2 w-full rounded border-transparent bg-transparent px-1 font-heading text-sm font-semibold hover:border-border focus:border-border"
                    aria-label={`Title for section ${section.key}`}
                  />
                  <div className="space-y-2">
                    {fields.filter((field) => field.sectionKey === section.key).map(renderField)}
                    {fields.filter((field) => field.sectionKey === section.key).length === 0 && (
                      <p className="rounded border border-dashed p-3 text-center text-xs text-muted-foreground">
                        Drag a field here, or click one in the palette.
                      </p>
                    )}
                  </div>
                </section>
              ))}
          </div>
        ))}
      </SortableContext>

      <button
        type="button"
        onClick={onAddSection}
        className="w-full rounded-md border border-dashed py-2 text-xs text-muted-foreground hover:bg-secondary"
      >
        Add section
      </button>
    </div>
  );
};
